import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Checkbox, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { OrderByControl, showToast } from '@/common/components'
import type { SortOption } from '@/common/components'
import { useDeleteHosts, useHostListPaginatedQuery } from '../api'
import type { HostListItem, HostSortOrder } from '../api'
import {
  BulkActionBar,
  BulkConfirmDialog,
  cellSx,
  formatDate,
  headerCellSx,
  InlineConfirmRow,
  PaginatedListModal,
  useListSelection,
  visuallyHiddenStyle,
} from './shared'
import type { BulkConfirmItem } from './shared'

interface HostsListModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

export const HostsListModal: FC<HostsListModalProps> = ({ open, onClose }) => {
  const [page, setPage] = useState(0)
  const [sortOrder, setSortOrder] = useState<HostSortOrder>('last_seen')
  const [isBulkConfirmOpen, setIsBulkConfirmOpen] = useState(false)
  const {
    selectedIds,
    confirmingId,
    setSelectedIds,
    setConfirmingId,
    handleSelectOne,
    handleRequestConfirm,
    handleCancelConfirm,
    resetSelection,
  } = useListSelection()

  const { data, isLoading, isError, error, refetch } = useHostListPaginatedQuery({
    page: 0,
    size: MAX_FETCH_SIZE,
    enabled: open,
    sort: sortOrder,
  })

  const { deleteHostsAsync, isDeleting, reset: resetDelete } = useDeleteHosts()

  const allContent = useMemo(() => data?.content ?? [], [data?.content])

  const content = useMemo(() => {
    // Sort client-side — backend ignores sort param
    return [...allContent].sort((a, b) => {
      switch (sortOrder) {
        case 'last_seen': {
          const aTime = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0
          const bTime = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0
          return bTime - aTime
        }
        case 'name_asc':
          return a.hostName.localeCompare(b.hostName)
      }
    })
  }, [allContent, sortOrder])

  const paginatedContent = useMemo(() => {
    const maxPage = Math.max(0, Math.ceil(content.length / PAGE_SIZE) - 1)
    const safePage = Math.min(page, maxPage)
    return content.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)
  }, [content, page])

  const allHostIds = useMemo(
    () => new Set(paginatedContent.map((host) => host.hostId)),
    [paginatedContent],
  )

  const allSelected = allHostIds.size > 0 && [...allHostIds].every((id) => selectedIds.has(id))
  const someSelected = !allSelected && [...allHostIds].some((id) => selectedIds.has(id))

  const handleClose = useCallback(() => {
    setPage(0)
    setSortOrder('last_seen')
    resetSelection()
    setIsBulkConfirmOpen(false)
    resetDelete()
    onClose()
  }, [onClose, resetDelete, resetSelection])

  const handleSortChange = useCallback(
    (newSort: HostSortOrder) => {
      setSortOrder(newSort)
      setPage(0)
      resetSelection()
    },
    [resetSelection],
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage)
      resetSelection()
    },
    [resetSelection],
  )

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const currentlyAllSelected = [...allHostIds].every((id) => prev.has(id))
      return currentlyAllSelected ? new Set<string>() : new Set(allHostIds)
    })
  }, [allHostIds, setSelectedIds])

  const handleDelete = useCallback(
    (hostId: string) => {
      void (async () => {
        try {
          const result = await deleteHostsAsync({ hostIds: [hostId] })
          if (result.success) {
            showToast('Host deleted', 'success')
            setConfirmingId(null)
            setSelectedIds((prev) => {
              const next = new Set(prev)
              next.delete(hostId)
              return next
            })
            if (paginatedContent.length === 1 && page > 0) {
              setPage((prev) => prev - 1)
            }
          } else {
            showToast(result.message, 'error')
          }
        } catch (err: unknown) {
          showToast(err instanceof Error ? err.message : 'Failed to delete host', 'error')
        }
      })().catch(() => {
        // Safety net: should never reach here if try/catch is comprehensive
      })
    },
    [deleteHostsAsync, paginatedContent.length, page, setConfirmingId, setSelectedIds],
  )

  const bulkConfirmItems: readonly BulkConfirmItem[] = useMemo(
    () =>
      paginatedContent
        .filter((host) => selectedIds.has(host.hostId))
        .map((host) => ({
          id: host.hostId,
          displayName: host.hostName,
        })),
    [paginatedContent, selectedIds],
  )

  const handleBulkDelete = useCallback(() => {
    void (async () => {
      try {
        const hostIds = bulkConfirmItems.map((item) => item.id)
        const result = await deleteHostsAsync({ hostIds })
        if (result.success) {
          showToast(`Deleted ${hostIds.length} hosts`, 'success')
          resetSelection()
          setIsBulkConfirmOpen(false)
          const remainingAfterDelete = paginatedContent.length - hostIds.length
          if (remainingAfterDelete === 0 && page > 0) {
            setPage((prev) => prev - 1)
          }
        } else {
          showToast(result.message, 'error')
        }
      } catch (err: unknown) {
        showToast(err instanceof Error ? err.message : 'Failed to delete hosts', 'error')
      }
    })().catch(() => {
      // Safety net: should never reach here if try/catch is comprehensive
    })
  }, [bulkConfirmItems, deleteHostsAsync, paginatedContent.length, page, resetSelection])

  const handleOpenBulkConfirm = useCallback(() => {
    setIsBulkConfirmOpen(true)
  }, [])

  const handleCloseBulkConfirm = useCallback(() => {
    setIsBulkConfirmOpen(false)
  }, [])

  const toolbar = (
    <Box sx={toolbarSx}>
      <OrderByControl<HostSortOrder>
        options={HOST_SORT_OPTIONS}
        value={sortOrder}
        onChange={handleSortChange}
        ariaLabel="Hosts sort order"
      />
    </Box>
  )

  return (
    <>
      <PaginatedListModal
        open={open}
        onClose={handleClose}
        title="Registered Hosts"
        totalElements={content.length}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={handlePageChange}
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={!isLoading && content.length === 0}
        emptyMessage="No registered hosts found."
        onRetry={refetch}
        toolbar={toolbar}
        bulkActionBar={
          <BulkActionBar
            selectedCount={selectedIds.size}
            actionLabel="Delete"
            onAction={handleOpenBulkConfirm}
            isActioning={isDeleting}
          />
        }
      >
        <Table size="small" aria-label="Registered hosts">
          <caption style={visuallyHiddenStyle}>
            List of registered hosts with name, creation date, last seen, and actions
          </caption>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx} padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  disabled={allHostIds.size === 0}
                  inputProps={{ 'aria-label': 'Select all items on this page' }}
                  size="small"
                />
              </TableCell>
              <TableCell sx={headerCellSx}>Host Name</TableCell>
              <TableCell sx={headerCellSx}>Created</TableCell>
              <TableCell sx={headerCellSx}>Last Seen</TableCell>
              <TableCell sx={headerCellSx}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedContent.map((host) => (
              <HostRow
                key={host.hostId}
                item={host}
                confirmingId={confirmingId}
                onRequestConfirm={handleRequestConfirm}
                onConfirm={handleDelete}
                onCancel={handleCancelConfirm}
                isDeleting={isDeleting}
                isSelected={selectedIds.has(host.hostId)}
                onSelect={handleSelectOne}
              />
            ))}
          </TableBody>
        </Table>
      </PaginatedListModal>

      <BulkConfirmDialog
        open={isBulkConfirmOpen}
        onClose={handleCloseBulkConfirm}
        onConfirm={handleBulkDelete}
        isActioning={isDeleting}
        actionLabel="Delete"
        entityLabel="Host"
        entityLabelPlural="Hosts"
        items={bulkConfirmItems}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Private: Row component
// ---------------------------------------------------------------------------

interface HostRowProps {
  readonly item: HostListItem
  readonly confirmingId: string | null
  readonly onRequestConfirm: (id: string) => void
  readonly onConfirm: (id: string) => void
  readonly onCancel: () => void
  readonly isDeleting: boolean
  readonly isSelected: boolean
  readonly onSelect: (id: string) => void
}

const HostRow: FC<HostRowProps> = ({
  item,
  confirmingId,
  onRequestConfirm,
  onConfirm,
  onCancel,
  isDeleting,
  isSelected,
  onSelect,
}) => {
  const confirmMessage = `Delete host "${item.hostName}"?`

  const handleSelect = (): void => {
    onSelect(item.hostId)
  }

  return (
    <InlineConfirmRow
      id={item.hostId}
      confirmingId={confirmingId}
      onRequestConfirm={onRequestConfirm}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isActioning={isDeleting}
      actionLabel="Delete"
      confirmMessage={confirmMessage}
      columnCount={COLUMN_COUNT}
    >
      <TableCell sx={cellSx} padding="checkbox">
        <Checkbox
          checked={isSelected}
          onChange={handleSelect}
          inputProps={{ 'aria-label': `Select ${item.hostName}` }}
          size="small"
        />
      </TableCell>
      <TableCell sx={cellSx}>{item.hostName}</TableCell>
      <TableCell sx={cellSx}>{formatDate(item.createdAt)}</TableCell>
      <TableCell sx={cellSx}>{formatDate(item.lastSeenAt)}</TableCell>
    </InlineConfirmRow>
  )
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HOST_SORT_OPTIONS: readonly SortOption<HostSortOrder>[] = [
  { value: 'last_seen', label: 'Last seen' },
  { value: 'name_asc', label: 'Name A \u2192 Z' },
] as const

const PAGE_SIZE = 20
const MAX_FETCH_SIZE = 1000
const COLUMN_COUNT = 5

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const toolbarSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  mb: 1,
}
